
import { defineComponent } from 'vue'
import ActiveUserMixin from '~/mixins/activeUserMixin'

import AuthMixin from '~/mixins/authMixin'
import ContextMixin from '~/mixins/contextMixin'
import PortalMixin from '~/mixins/portalMixin'
import { TeamPerms } from '~/types/permTypes'
import { TeamMemberRoleChoices } from '~/types/types'

interface ITabsList {
  label: string
  icon: string
  link: string
  validator?: boolean
}

export default defineComponent({
  name: 'DashboardLayout',
  mixins: [AuthMixin, ContextMixin, PortalMixin, ActiveUserMixin],
  head: {
    bodyAttrs: {
      class: 'antialiased stroke-2 hide-scroll'
    }
  },
  async fetch(): Promise<void> {
    await this.fetchActiveUser({})
  },
  data() {
    return {
      hasIdentified: false,
      lastGroupedTeamId: null as number | null
    }
  },
  watch: {
    viewer: {
      immediate: true,
      handler(user) {
        if (this.hasIdentified || !user?.id || !user?.email) return

        const userId = Buffer.from(user.id, 'base64').toString().toLowerCase().replace('user:', '')

        this.$sentry.setUser({ email: user.email, id: userId })

        this.$trackingAnalytics?.identify(userId, {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName
        })
        this.hasIdentified = true
      }
    },
    activeDashboardContext: {
      immediate: true,
      handler(ctx) {
        if (!ctx?.id || typeof ctx.id !== 'number') return
        if (ctx.id === this.lastGroupedTeamId) return
        this.lastGroupedTeamId = ctx.id

        // Keep the org cookie in sync so non-dashboard pages
        // (e.g. /support) resolve the correct context
        if (ctx.vcs_provider && ctx.login) {
          this.setActiveContextCookie(ctx.vcs_provider, ctx.login)
        }

        this.$trackingAnalytics?.group('team', String(ctx.id), {
          name: ctx.login,
          owner_type: ctx.type,
          vcs_provider: ctx.vcs_provider_display
        })
      }
    }
  },
  computed: {
    isSubscriptionPaused(): boolean {
      const ctx = this.activeDashboardContext
      return ctx && 'is_subscription_paused' in ctx && ctx.is_subscription_paused === true
    },
    isWhitelistedRoute(): boolean {
      const path = this.$route.path
      const settingsPath = this.$generateRoute(['settings'], false, true)
      const membersPath = this.$generateRoute(['members'], false, true)
      return path.startsWith(settingsPath) || path.startsWith(membersPath)
    },
    isHomeRoute(): boolean {
      return this.$route.name === 'provider-owner'
    },
    timeOfDay(): string {
      const hour = new Date().getHours()
      if (hour < 12) return 'Good morning'
      if (hour < 18) return 'Good afternoon'
      return 'Good evening'
    },
    isGroupPage(): boolean {
      return Boolean(this.$route.name?.startsWith('orgs-provider-owner-groups'))
    },
    tabsList(): ITabsList[] {
      return [
        {
          label: 'Home',
          icon: 'home',
          link: this.$generateRoute([''])
        },
        {
          label: 'Repositories',
          icon: 'layers',
          link: this.getLink('repos')
        },
        {
          label: 'Reports',
          icon: 'pie-chart',
          link: this.getLink('reports'),
          validator: this.canViewReports
        },
        {
          label: 'Members',
          icon: 'users',
          link: this.getLink('members'),
          validator: this.showTeamMembers
        },
        {
          label: 'Settings',
          icon: 'settings',
          link: this.getLink('settings'),
          validator: this.showTeamSettings
        }
      ] as ITabsList[]
    },
    canViewReports(): boolean {
      return this.$gateKeeper.team(TeamPerms.VIEW_REPORTS, this.teamPerms.permission)
    },
    showTeamMembers(): boolean {
      if (this.activeDashboardContext && 'type' in this.activeDashboardContext) {
        if (this.activeDashboardContext.type === 'user') {
          return false
        }

        const role = this.activeDashboardContext.role as TeamMemberRoleChoices
        return this.$gateKeeper.team(
          [TeamPerms.MANAGE_TEAM_MEMBERS, TeamPerms.VIEW_TEAM_MEMBERS],
          role
        )
      }
      return false
    },
    showTeamSettings(): boolean {
      if (this.activeDashboardContext && 'type' in this.activeDashboardContext) {
        if (this.activeDashboardContext.type === 'user') {
          return false
        }
      }

      if (this.activeDashboardContext && 'role' in this.activeDashboardContext) {
        const role = this.activeDashboardContext.role as TeamMemberRoleChoices
        return this.$gateKeeper.team(
          [
            TeamPerms.CHANGE_PLAN,
            TeamPerms.UPDATE_SEATS,
            TeamPerms.UPDATE_BILLING_DETAILS,
            TeamPerms.MANAGE_TEAM_MEMBERS,
            TeamPerms.VIEW_ACCESS_CONTROL_DASHBOARD,
            TeamPerms.DELETE_TEAM_ACCOUNT,
            TeamPerms.VIEW_TEAM_GENERAL_SETTINGS
          ],
          role
        )
      }

      return false
    }
  },
  methods: {
    getLink(path = ''): string {
      return this.$generateRoute([path], false, true)
    },
    getTabActiveStatus(tab: ITabsList): boolean {
      if (tab.label === 'Home') {
        return this.$route.name === 'provider-owner'
      }

      // Return the top level route path for `settings`
      return this.$route.path.startsWith(
        tab.label === 'Settings' ? this.getLink('settings') : tab.link
      )
    }
  }
})
